import React, {
  useEffect, useMemo, lazy, Suspense
} from 'react';
import { string, bool } from 'prop-types';
import { useProductDeprecated } from '@thd-nucleus/data-sources/react/dataModel/migration';
import { useStore } from '@thd-nucleus/experience-context';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { useLayoutGroupProps } from '@thd-olt-component-react/layout-manager';
import { LoadingPlaceholder } from '@thd-olt-component-react/loading-placeholder';

import { isValidPromotionData, isSanitaryItemId } from '../utils/promo-model-utils';
import { dataModel } from './dataModel';

const PromotionProductsLazy = lazy(() => import(
  /* webpackChunkName: "promotion-products-lazy" */
  './PromotionProductsLazy'
).then((mod) => {
  return {
    default: (props) => {
      const { PromotionProductsLazy: PromotionProductsComp } = mod;

      return (
        <QueryProvider cacheKey="discounted-items">
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <PromotionProductsComp {...props} />
        </QueryProvider>
      );
    }
  };
}));

// eslint-disable-next-line react/prop-types
const SuspenseFallback = ({ experienceTag }) => {
  return (
    <>
      {experienceTag === 'BOGO' && (
        <LoadingPlaceholder
          key="promotion-products-suspense"
          type="text"
          color="#E0E0E0"
          rows={4}
          ready={false}
          showLoadingAnimation
          style={{
            padding: '5px 0'
          }}
        >
          <div />
        </LoadingPlaceholder>
      )}

      {experienceTag === 'MSBONLINE' && (
        <LoadingPlaceholder
          key="promotion-products-suspense"
          type="text"
          color="#E0E0E0"
          rows={6}
          ready={false}
          showLoadingAnimation
          style={{
            padding: '5px 0'
          }}
        >
          <div />
        </LoadingPlaceholder>
      )}

      {experienceTag === 'BMSM' && (
        <LoadingPlaceholder
          key="promotion-products-suspense"
          type="text"
          color="#E0E0E0"
          rows={5}
          ready={false}
          showLoadingAnimation
          style={{
            padding: '9px 0'
          }}
        >
          <div />
        </LoadingPlaceholder>
      )}
    </>
  );
};

const returnProductIfValid = (data) => (
  (data?.product?.pricing?.promotion
  && isValidPromotionData(data.product.pricing)
  && isSanitaryItemId(data?.product.itemId))
    ? data.product
    : null
);

const shouldDisplayPromotion = (bogoEnabled, msbEnabled, bmsmEnabled, experienceTag) => {
  return (bogoEnabled && experienceTag === 'BOGO')
      || (msbEnabled && experienceTag === 'MSBONLINE')
      || (bmsmEnabled && experienceTag === 'BMSM');
};

export const PromotionProducts = ({ itemId, ...props }) => {

  const {
    bogoEnabled, msbEnabled, bmsmEnabled
  } = useLayoutGroupProps({ props });

  useEffect(() => {
    if (bogoEnabled || msbEnabled || bmsmEnabled) {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('promotion-products.ready');
    }
  }, [bogoEnabled, msbEnabled, bmsmEnabled]);

  const store = useStore();

  const { data } = useProductDeprecated({
    itemId,
    storeId: store.storeId,
    domains: ['$ProductPod'],
    ssr: false,
    skip: (!bogoEnabled && !msbEnabled && !bmsmEnabled) || !isSanitaryItemId(itemId),
  }, 'clientOnlyProduct');

  const anchorItem = useMemo(() => returnProductIfValid(data), [data]);

  const experienceTag = anchorItem?.pricing?.promotion?.experienceTag;

  return shouldDisplayPromotion(bogoEnabled, msbEnabled, bmsmEnabled, experienceTag) ? (
    <Suspense fallback={<SuspenseFallback experienceTag={experienceTag} />}>
      <PromotionProductsLazy
        anchorItem={anchorItem}
        store={store}
      />
    </Suspense>
  ) : null;
};

PromotionProducts.displayName = 'PromotionProducts';

PromotionProducts.propTypes = {
  itemId: string,
  /* eslint-disable-next-line react/no-unused-prop-types */
  bogoEnabled: bool,
  /* eslint-disable-next-line react/no-unused-prop-types */
  msbEnabled: bool,
  /* eslint-disable-next-line react/no-unused-prop-types */
  bmsmEnabled: bool,
};

PromotionProducts.defaultProps = {
  itemId: '',
  bogoEnabled: true,
  msbEnabled: true,
  bmsmEnabled: false,
};

PromotionProducts.dataModel = dataModel;
